import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import cx from 'classnames';
import Resizable from 're-resizable';
import { compose } from 'recompose';

import HeaderImageToolbar from '../HeaderImageToolBar';
import withHeaderContentAlignmentStyles from '../hoc/withHeaderContentAlignmentStyles';
import { setHeaderLogoImageSize } from '../../../actions/headerEditorActions';
import * as headerLogoImageSizePresets from '../constants/headerLogoImageSizePresets';
import { calculateLogoSize } from '../utils/logoSizeUtils';
import {
  headerLogoSizePresetPropTypes,
  sizePropTypes,
} from '../../../config/sectionHeader/sectionHeaderPropTypes';
import { minSize } from '../configs/headerLogoImageSizesConfig';

import styles from './HeaderLogoEditor.scss';

class HeaderLogoEditor extends Component {
  static propTypes = {
    logoImageUrl: PropTypes.string.isRequired,
    logoImageLinkUrl: PropTypes.string,
    headerSize: sizePropTypes,
    setHeaderLogoImageSize: PropTypes.func.isRequired,
    sizePreset: headerLogoSizePresetPropTypes,
    size: sizePropTypes,
    className: PropTypes.string,
    restrictionEnabled: PropTypes.bool,
  };

  static defaultProps = {
    logoImageLinkUrl: '',
    size: null,
    className: '',
    restrictionEnabled: true,
  };

  state = {
    isTooltipEnabled: false,
    imageSize: minSize,
    loaded: false,
  };

  componentWillReceiveProps(nextProps) {
    if (this.props.logoImageUrl !== nextProps.logoImageUrl) {
      this.setState({ loaded: false });
    }
  }

  onClickHandler = () => {
    const { logoImageLinkUrl } = this.props;
    !!logoImageLinkUrl && window.open(logoImageLinkUrl, '_blank');
  };

  onMouseEnterHandler = () => {
    this.setState({ isTooltipEnabled: true });
  };

  onMouseLeaveHandler = () => {
    this.setState({ isTooltipEnabled: false });
  };

  renderToolbar() {
    return this.state.isTooltipEnabled ? (
      <HeaderImageToolbar
        className={styles.tools}
        onChangePreset={this.onChangePreset}
      />
    ) : null;
  }

  onChangePreset = preset => {
    this.saveLogoSize({ size: null, sizePreset: preset });
  };

  onImageLoadHandler = ({ target: { height, width } }) => {
    const imageSize = { width, height };

    this.setState({ loaded: true, imageSize }, () => {
      const { size } = this.props;

      // @note do not save logo size if logo size was saved on server @e.batrak
      if (size) {
        return;
      }

      this.saveLogoSize({
        imageSize,
      });
    });
  };

  onResizeStop = (event, direction, refToElement, delta) => {
    const { width, height } = this.getLogoSize();

    const logoSize = {
      width: width + delta.width,
      height: height + delta.height,
    };
    this.saveLogoSize({
      size: logoSize,
      sizePreset: headerLogoImageSizePresets.CUSTOM,
    });
  };

  getLogoSize = ({
    size = this.props.size,
    sizePreset = this.props.sizePreset,
    headerSize = this.props.headerSize,
    imageSize = this.state.imageSize,
    restrictionEnabled = this.props.restrictionEnabled,
  } = {}) =>
    calculateLogoSize({
      size,
      sizePreset,
      headerSize,
      imageSize,
      restrictionEnabled,
    });

  saveLogoSize = ({
    size = this.props.size,
    sizePreset = this.props.sizePreset,
    headerSize = this.props.headerSize,
    imageSize = this.state.imageSize,
  } = {}) => {
    const logoSize = this.getLogoSize({
      size,
      sizePreset,
      headerSize,
      imageSize,
    });

    this.props.setHeaderLogoImageSize(logoSize, sizePreset);
  };

  renderImage() {
    const {
      logoImageUrl,
      logoImageLinkUrl,
      headerSize: { width: headerWidth, height: headerHeight },
      restrictionEnabled,
    } = this.props;
    const { width: minWidth, height: minHeight } = minSize;
    const { loaded } = this.state;
    const maxSize = restrictionEnabled
      ? { maxWidth: headerWidth, maxHeight: headerHeight }
      : {};

    return logoImageUrl ? (
      <Resizable
        className={cx(styles.resizableContainer, { [styles.hidden]: !loaded })}
        handleClasses={{
          topRight: styles.handler,
          topLeft: styles.handler,
          bottomRight: styles.handler,
          bottomLeft: styles.handler,
        }}
        lockAspectRatio
        size={this.getLogoSize()}
        minWidth={minWidth}
        minHeight={minHeight}
        maxWidth={restrictionEnabled ? headerWidth : undefined}
        maxHeight={restrictionEnabled ? headerHeight : undefined}
        {...maxSize}
        onResizeStop={this.onResizeStop}
      >
        <img
          src={logoImageUrl}
          className={cx(styles.image, {
            [styles.linkedImage]: logoImageLinkUrl,
          })}
          onClick={this.onClickHandler}
          onLoad={this.onImageLoadHandler}
        />
      </Resizable>
    ) : null;
  }

  render() {
    const { className } = this.props;

    return (
      <div className={cx(styles.container, className)}>
        <div
          className={styles.imageContainer}
          onMouseEnter={this.onMouseEnterHandler}
          onMouseLeave={this.onMouseLeaveHandler}
        >
          {this.renderToolbar()}
          {this.renderImage()}
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = {
  setHeaderLogoImageSize,
};

export default compose(
  connect(
    null,
    mapDispatchToProps,
  ),
  withHeaderContentAlignmentStyles,
)(HeaderLogoEditor);
